import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  approveManagementOrder,
  createOrderShipment,
  deleteOrderShipment,
  getManagementOrder,
  patchOrderShipment,
  rejectManagementOrder,
  reopenManagementOrder,
} from '../../../endpoints/chapterSupplies';
import { getCountries, getStates } from '../../../endpoints/lookupsTs';
import { getMyPermissions } from '../../../endpoints/usersTs';
import type { StorefrontCheckoutLineResponse } from '../../../types/chapterSupplies';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const CARRIER_OPTIONS: { id: string; label: string }[] = [
  { id: '40102e8d6d264d6d9c3ad810f6354cac', label: 'UPS' },
  { id: '6af9b847433341bba003514ec69bb8a5', label: 'USPS' },
  { id: '7acb0bf2a4584ee0965e721362b5de3d', label: 'FedEx' },
];

function carrierLabel(carrierId: string): string {
  return CARRIER_OPTIONS.find((c) => c.id === carrierId)?.label || carrierId;
}

function lineDisplayName(
  lines: StorefrontCheckoutLineResponse[],
  orderLineItemId: string
): string {
  const match = lines.find((l) => l.order_line_item_id === orderLineItemId);
  const name = match?.item_name?.trim();
  return name || orderLineItemId;
}

// Match chapter-supplies order_statuses IDs / names (see peo-hub-go internal/features/chapter_supplies/constants.go).
const ORDER_STATUS_PENDING_APPROVAL_ID = '317bce051aa54ba8a20453d8e7d422cd';
const ORDER_STATUS_REJECTED_ID = 'fb904cd1c53f41b198727b3687b02900';

function isPendingApprovalStatus(
  statusName: string,
  statusId: string
): boolean {
  const slug = statusName.trim().toLowerCase();
  if (slug === 'pending_approval') return true;
  return statusId.trim() === ORDER_STATUS_PENDING_APPROVAL_ID;
}

function isRejectedStatus(statusName: string, statusId: string): boolean {
  const slug = statusName.trim().toLowerCase();
  if (slug === 'rejected') return true;
  return statusId.trim() === ORDER_STATUS_REJECTED_ID;
}

export default function ManagementOrderDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useApiGet(
    ['chapter-supplies-management-order', id],
    () => getManagementOrder(id!),
    { enabled: !!id }
  );
  const { data: myPermissions = [] } = useApiGet(
    ['chapter-supplies-order-detail-perms'],
    () => getMyPermissions(),
    { staleTime: 60_000 }
  );
  const { data: myIntlPerms = [] } = useApiGet(
    ['chapter-supplies-order-detail-perms-intl'],
    () => getMyPermissions({ international: 'true' }),
    { staleTime: 60_000 }
  );
  const canShip = React.useMemo(() => {
    const s = new Set([...myPermissions, ...myIntlPerms]);
    return s.has('orders:shipment');
  }, [myPermissions, myIntlPerms]);

  const canApprove = React.useMemo(() => {
    const s = new Set([...myPermissions, ...myIntlPerms]);
    return s.has('orders:approver');
  }, [myPermissions, myIntlPerms]);

  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [shipErr, setShipErr] = React.useState<string | null>(null);
  const [shipBusy, setShipBusy] = React.useState(false);
  const [newBoxQty, setNewBoxQty] = React.useState<Record<string, string>>({});
  const [draftForms, setDraftForms] = React.useState<
    Record<string, { carrier_id: string; tracking: string; notes: string }>
  >({});

  React.useEffect(() => {
    if (!data?.shipping) {
      setDraftForms({});
      return;
    }
    const m: Record<
      string,
      { carrier_id: string; tracking: string; notes: string }
    > = {};
    for (const sh of data.shipping.shipments) {
      if (!sh.shipped_at) {
        m[sh.id] = {
          carrier_id: sh.carrier_id || CARRIER_OPTIONS[0].id,
          tracking: sh.tracking_number || '',
          notes: sh.notes || '',
        };
      }
    }
    setDraftForms(m);
  }, [data?.shipping]);

  const runAction = async (action: 'approve' | 'reject' | 'reopen') => {
    if (!id) return;
    setActionLoading(true);
    setActionError(null);
    try {
      if (action === 'approve') await approveManagementOrder(id);
      if (action === 'reject') await rejectManagementOrder(id);
      if (action === 'reopen') await reopenManagementOrder(id);
      navigate(0);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Unable to update order status.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const invalidateOrder = React.useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['chapter-supplies-management-order', id],
    });
  }, [id, queryClient]);

  const handleCreateShipment = async () => {
    if (!id || !data?.lines) return;
    setShipBusy(true);
    setShipErr(null);
    try {
      const linesPayload = data.lines
        .map((line) => {
          const key = line.order_line_item_id;
          if (!key) return null;
          const q = parseInt(newBoxQty[key] || '0', 10);
          if (!q || q < 0) return null;
          return { order_line_item_id: key, quantity: q };
        })
        .filter(Boolean) as { order_line_item_id: string; quantity: number }[];
      if (linesPayload.length === 0) {
        setShipErr('Enter a positive quantity for at least one line.');
        return;
      }
      await createOrderShipment(id, { lines: linesPayload });
      setNewBoxQty({});
      invalidateOrder();
    } catch (e) {
      setShipErr(e instanceof Error ? e.message : 'Unable to create shipment.');
    } finally {
      setShipBusy(false);
    }
  };

  const handleMarkShipped = async (shipmentId: string) => {
    if (!id) return;
    const f = draftForms[shipmentId];
    if (!f) return;
    setShipBusy(true);
    setShipErr(null);
    try {
      await patchOrderShipment(id, shipmentId, {
        carrier_id: f.carrier_id,
        tracking_number: f.tracking.trim(),
        notes: f.notes.trim() || undefined,
        mark_shipped: true,
      });
      invalidateOrder();
    } catch (e) {
      setShipErr(e instanceof Error ? e.message : 'Unable to mark shipped.');
    } finally {
      setShipBusy(false);
    }
  };

  const handleDeleteDraft = async (shipmentId: string) => {
    if (!id) return;
    setShipBusy(true);
    setShipErr(null);
    try {
      await deleteOrderShipment(id, shipmentId);
      invalidateOrder();
    } catch (e) {
      setShipErr(e instanceof Error ? e.message : 'Unable to delete shipment.');
    } finally {
      setShipBusy(false);
    }
  };

  const { data: countries = [] } = useApiGet(
    ['management-order-detail-countries'],
    getCountries
  );
  const { data: states = [] } = useApiGet(
    ['management-order-detail-states', data?.ship_to_country_id || ''],
    () => getStates(data?.ship_to_country_id || ''),
    { enabled: !!data?.ship_to_country_id }
  );

  const resolvedCountryName =
    (data?.ship_to_country_name && data.ship_to_country_name.trim()) ||
    countries.find((country) => country.id === data?.ship_to_country_id)
      ?.name ||
    'Unknown country';
  const resolvedStateName =
    (data?.ship_to_state_name && data.ship_to_state_name.trim()) ||
    states.find((state) => state.id === data?.ship_to_state_id)?.name ||
    'Unknown state/province';

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/chapter-supplies-management/orders')}
        sx={{ mb: 2 }}
      >
        Back to orders
      </Button>
      {isLoading && <Typography>Loading order...</Typography>}
      {error && (
        <Typography color="error">Unable to load order details.</Typography>
      )}
      {data && (
        <Card>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 1 }}>
              {data.order_number != null
                ? `Order #${data.order_number}`
                : `Order ${data.order_id}`}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {new Date(data.placed_at).toLocaleString()} —{' '}
              {data.order_status_name || data.order_status_id}
            </Typography>
            {data.is_vendor_direct && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This is a vendor-direct emblem order. It bypasses the internal
                shipment workflow.
              </Alert>
            )}
            {data.approved_at && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Approved: {new Date(data.approved_at).toLocaleString()}
                {data.approved_by_id ? ` by ${data.approved_by_id}` : ''}
              </Typography>
            )}
            {canApprove &&
              (isPendingApprovalStatus(
                data.order_status_name || '',
                data.order_status_id
              ) ||
                isRejectedStatus(
                  data.order_status_name || '',
                  data.order_status_id
                )) && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {isPendingApprovalStatus(
                    data.order_status_name || '',
                    data.order_status_id
                  ) && (
                    <>
                      <Button
                        variant="contained"
                        onClick={() => void runAction('approve')}
                        disabled={actionLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => void runAction('reject')}
                        disabled={actionLoading}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {isRejectedStatus(
                    data.order_status_name || '',
                    data.order_status_id
                  ) && (
                    <Button
                      variant="outlined"
                      onClick={() => void runAction('reopen')}
                      disabled={actionLoading}
                    >
                      Reopen
                    </Button>
                  )}
                </Stack>
              )}
            {actionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {actionError}
              </Alert>
            )}

            <Typography variant="h6" sx={{ mb: 1 }}>
              Purchaser
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {data.purchaser_display_name}
              {data.purchaser_email ? (
                <>
                  <br />
                  {data.purchaser_email}
                </>
              ) : null}
              <br />
              User ID: {data.user_id}
            </Typography>

            {data.is_vendor_direct && (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Vendor fulfillment
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Mode: {data.fulfillment_mode || 'vendor_direct'}
                  {data.vendor_name ? (
                    <>
                      <br />
                      Vendor: {data.vendor_name}
                    </>
                  ) : null}
                  {data.vendor_batch_status ? (
                    <>
                      <br />
                      Batch status: {data.vendor_batch_status}
                    </>
                  ) : null}
                  {data.vendor_batch_sent_at ? (
                    <>
                      <br />
                      Sent to vendor:{' '}
                      {new Date(data.vendor_batch_sent_at).toLocaleString()}
                    </>
                  ) : null}
                </Typography>
                {data.vendor_batch_id && (
                  <Button
                    variant="outlined"
                    sx={{ mb: 2 }}
                    onClick={() =>
                      navigate(
                        `/chapter-supplies-management/vendor-batches/${data.vendor_batch_id}`
                      )
                    }
                  >
                    View vendor batch
                  </Button>
                )}
              </>
            )}

            <Typography variant="h6" sx={{ mb: 1 }}>
              Ship to
            </Typography>
            <Typography variant="body2">
              {data.ship_to_name}
              <br />
              {data.ship_to_address_line1}
              {data.ship_to_address_line2
                ? `, ${data.ship_to_address_line2}`
                : ''}
              <br />
              {data.ship_to_city}, {resolvedStateName}{' '}
              {data.ship_to_postal_code}
              <br />
              {resolvedCountryName}
            </Typography>

            {canShip && !data.shipping && !data.is_vendor_direct && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                Fulfillment details load when your account has the
                orders:shipment permission.
              </Typography>
            )}

            {canShip && data.shipping && !data.is_vendor_direct && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Fulfillment
                </Typography>
                {shipErr && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {shipErr}
                  </Alert>
                )}
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Line progress
                </Typography>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Ordered</TableCell>
                      <TableCell align="right">Shipped</TableCell>
                      <TableCell align="right">In draft boxes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.shipping.fulfillment.map((row) => (
                      <TableRow key={row.order_line_item_id}>
                        <TableCell>
                          {lineDisplayName(data.lines, row.order_line_item_id)}
                        </TableCell>
                        <TableCell align="right">
                          {row.quantity_ordered}
                        </TableCell>
                        <TableCell align="right">
                          {row.quantity_shipped}
                        </TableCell>
                        <TableCell align="right">
                          {row.quantity_allocated_draft}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Shipments
                </Typography>
                {data.shipping.shipments.length === 0 ? (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    No boxes yet.
                  </Typography>
                ) : (
                  <Stack spacing={2} sx={{ mb: 2 }}>
                    {data.shipping.shipments.map((sh) => {
                      const isDraft = !sh.shipped_at;
                      const f = draftForms[sh.id];
                      return (
                        <Box
                          key={sh.id}
                          sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Box {sh.id.slice(0, 8)}…
                            {isDraft ? (
                              <Typography
                                component="span"
                                variant="caption"
                                color="warning.main"
                                sx={{ ml: 1 }}
                              >
                                Draft
                              </Typography>
                            ) : (
                              <Typography
                                component="span"
                                variant="caption"
                                color="success.main"
                                sx={{ ml: 1 }}
                              >
                                Shipped{' '}
                                {sh.shipped_at
                                  ? new Date(sh.shipped_at).toLocaleString()
                                  : ''}
                              </Typography>
                            )}
                          </Typography>
                          {sh.lines.map((ln) => (
                            <Typography
                              key={ln.id}
                              variant="body2"
                              color="text.secondary"
                            >
                              {lineDisplayName(
                                data.lines,
                                ln.order_line_item_id
                              )}{' '}
                              × {ln.quantity}
                            </Typography>
                          ))}
                          {isDraft && f && (
                            <Stack spacing={1.5} sx={{ mt: 2 }}>
                              <FormControl size="small" fullWidth>
                                <InputLabel id={`carr-${sh.id}`}>
                                  Carrier
                                </InputLabel>
                                <Select
                                  labelId={`carr-${sh.id}`}
                                  label="Carrier"
                                  value={f.carrier_id}
                                  onChange={(e) =>
                                    setDraftForms((prev) => ({
                                      ...prev,
                                      [sh.id]: {
                                        ...f,
                                        carrier_id: String(e.target.value),
                                      },
                                    }))
                                  }
                                >
                                  {CARRIER_OPTIONS.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                      {c.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <TextField
                                size="small"
                                label="Tracking number"
                                value={f.tracking}
                                onChange={(e) =>
                                  setDraftForms((prev) => ({
                                    ...prev,
                                    [sh.id]: { ...f, tracking: e.target.value },
                                  }))
                                }
                                fullWidth
                              />
                              <TextField
                                size="small"
                                label="Notes (optional)"
                                value={f.notes}
                                onChange={(e) =>
                                  setDraftForms((prev) => ({
                                    ...prev,
                                    [sh.id]: { ...f, notes: e.target.value },
                                  }))
                                }
                                fullWidth
                                multiline
                                minRows={2}
                              />
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="contained"
                                  disabled={shipBusy}
                                  onClick={() => void handleMarkShipped(sh.id)}
                                >
                                  Mark shipped
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  disabled={shipBusy}
                                  onClick={() => void handleDeleteDraft(sh.id)}
                                >
                                  Delete draft
                                </Button>
                              </Stack>
                            </Stack>
                          )}
                          {!isDraft && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {carrierLabel(sh.carrier_id || '')} —{' '}
                              {sh.tracking_number || '—'}
                              {sh.notes ? (
                                <>
                                  <br />
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {sh.notes}
                                  </Typography>
                                </>
                              ) : null}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  New box (draft)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  Enter quantities for this box, then create. You can add
                  carrier and tracking when marking shipped.
                </Typography>
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {data.lines.map((line) => {
                    const key = line.order_line_item_id;
                    if (!key) return null;
                    const ful = data.shipping!.fulfillment.find(
                      (r) => r.order_line_item_id === key
                    );
                    const remaining = ful
                      ? ful.quantity_ordered -
                        ful.quantity_shipped -
                        ful.quantity_allocated_draft
                      : line.quantity;
                    return (
                      <Stack
                        key={key}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {(line.item_name || 'Item').trim()} × {line.quantity}
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            (up to {remaining} left to allocate)
                          </Typography>
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          label="This box"
                          sx={{ width: 120 }}
                          inputProps={{ min: 0, max: remaining }}
                          value={newBoxQty[key] ?? ''}
                          onChange={(e) =>
                            setNewBoxQty((prev) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                        />
                      </Stack>
                    );
                  })}
                </Stack>
                <Button
                  variant="outlined"
                  disabled={shipBusy}
                  onClick={() => void handleCreateShipment()}
                >
                  Create draft box
                </Button>
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Items
            </Typography>
            <Stack spacing={1}>
              {data.lines.map((line, idx) => (
                <Box
                  key={
                    line.order_line_item_id ?? `${line.online_item_id}-${idx}`
                  }
                  sx={{ mb: 1 }}
                >
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">
                      {line.item_name?.trim() || 'Item'} × {line.quantity}
                    </Typography>
                    <Typography variant="body2">
                      {formatPrice(line.subtotal)}
                    </Typography>
                  </Stack>
                  {line.custom_text_lines &&
                    line.custom_text_lines.length > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 0.5, pl: 1 }}
                      >
                        {line.custom_text_lines.map((t, i) => (
                          <span key={i}>
                            {i > 0 ? ' · ' : ''}
                            {t}
                          </span>
                        ))}
                      </Typography>
                    )}
                  {line.personalization_display_name && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 0.5, pl: 1 }}
                    >
                      Member: {line.personalization_display_name}
                      {line.personalization_first_name
                        ? ` — Name on item: ${line.personalization_first_name}`
                        : ''}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography>Subtotal</Typography>
              <Typography>{formatPrice(data.subtotal)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Freight</Typography>
              <Typography>{formatPrice(data.shipping_amount)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Tax</Typography>
              <Typography>{formatPrice(data.tax)}</Typography>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography variant="h6">Total</Typography>
              <Typography variant="h6" color="primary.main">
                {formatPrice(data.total)}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
